import {getFeatureFlags} from '@chekin/core';
import type {PostHog as PostHogJs} from 'posthog-js';

const IS_POSTHOG_ENABLED = getFeatureFlags('POSTHOG_TRACKING');

let loadPromise: Promise<PostHogJs | null> | null = null;

// Kept in memory as well as in PostHog itself: events can be captured before the
// reservation (and therefore the super-properties) has loaded, and the SDK runs
// inside an iframe where PostHog's persistence may be unavailable.
let superProperties: Record<string, unknown> = {};

function loadPostHog(): Promise<PostHogJs | null> {
  if (!IS_POSTHOG_ENABLED) {
    return Promise.resolve(null);
  }
  if (!loadPromise) {
    loadPromise = import('posthog-js').then(({default: posthog}) => {
      posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        autocapture: false,
        capture_pageleave: false,
        capture_pageview: false,
        capture_performance: {web_vitals: false},
        // api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        // capture_pageleave: false,
        // capture_pageview: false,
        // capture_performance: {web_vitals: false},
        // autocapture: {dom_event_allowlist: ['click']},
        // rageclick: true,
        // capture_dead_clicks: true,
        // capture_heatmaps: true,
        // // PII masking is mandatory: mask every input, block camera/biometric
        // // regions tagged with `.ph-no-capture`, mask free-text PII with `.ph-mask`.
        // disable_session_recording: false,
        // session_recording: {
        //   maskAllInputs: true,
        //   maskTextSelector: '.ph-mask',
        //   blockSelector: '.ph-no-capture',
        //   maskInputOptions: {password: true},
        //   recordCrossOriginIframes: false,
        // },
      });
      return posthog;
    });
  }
  return loadPromise;
}

class PostHog {
  static initialize() {
    if (!IS_POSTHOG_ENABLED) return;
    void loadPostHog();
  }

  static identify(distinctId?: string) {
    if (!IS_POSTHOG_ENABLED || !distinctId) return;
    void loadPostHog().then(posthog => {
      if (!posthog) return;

      if (posthog._isIdentified() && posthog.get_distinct_id() !== distinctId) {
        posthog.reset();
        posthog.register(superProperties);
      }

      posthog.identify(distinctId);
    });
  }

  static register(properties: Record<string, unknown>) {
    superProperties = {...superProperties, ...properties};
    if (!IS_POSTHOG_ENABLED) return;
    void loadPostHog().then(posthog => posthog?.register(properties));
  }

  static setPersonProperties(properties: Record<string, unknown>) {
    if (!IS_POSTHOG_ENABLED) return;
    void loadPostHog().then(posthog => posthog?.setPersonProperties(properties));
  }

  static track(event: string, data?: Record<string, unknown>) {
    if (!IS_POSTHOG_ENABLED) return;
    const properties = {...superProperties, ...data};
    void loadPostHog().then(posthog => posthog?.capture(event, properties));
  }
}

export {PostHog};
